import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { 
  Code2, 
  Smartphone, 
  Database, 
  Palette, 
  Cloud, 
  Zap, 
  Check
} from 'lucide-react';

interface Skill {
  title: string;
  percentage: number;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  technologies: string[];
}

const Skills: React.FC = () => {
  const [animate, setAnimate] = useState(false);
  const [visibleBars, setVisibleBars] = useState<boolean[]>([]);
  const sectionRef = useRef<HTMLElement>(null);

  const skills: Skill[] = [
    {
      title: 'Web Development',
      percentage: 95,
      description: 'Expert in modern web frameworks and backend technologies for scalable web applications.',
      icon: Code2,
      color: 'bg-blue-600',
      technologies: ['PHP', 'Laravel', 'Next.js', 'Node.js', 'Express.js', 'Nette', 'Symfony', 'WordPress']
    },
    {
      title: 'Frontend Technologies',
      percentage: 85,
      description: 'Creating responsive, interactive user interfaces with modern JavaScript frameworks.',
      icon: Palette,
      color: 'bg-green-600',
      technologies: ['JavaScript', 'jQuery', 'React', 'TypeScript', 'CSS', 'SASS', 'Tailwind CSS', 'Bootstrap']
    },
    {
      title: 'Database Management',
      percentage: 90,
      description: 'Designing and optimizing databases for performance and scalability.',
      icon: Database,
      color: 'bg-purple-600',
      technologies: ['MySQL', 'PostgreSQL', 'SQL', 'MongoDB']
    },
    {
      title: 'API Development',
      percentage: 88,
      description: 'Building and integrating REST APIs for seamless service communication.',
      icon: Cloud,
      color: 'bg-pink-600',
      technologies: ['REST APIs', 'API Integration', 'JSON', 'XML Processing']
    },
    {
      title: 'Mobile Development',
      percentage: 85,
      description: 'Cross-platform mobile app development with native performance.',
      icon: Smartphone,
      color: 'bg-orange-600',
      technologies: ['Flutter', 'React Native', 'iOS', 'Android']
    },
    {
      title: 'Tools & DevOps',
      percentage: 82,
      description: 'Modern development tools and automation for efficient workflows.',
      icon: Zap,
      color: 'bg-cyan-600',
      technologies: ['n8n automation', 'AWS', 'Docker', 'GitHub', 'C#', 'VB .Net']
    }
  ];

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setAnimate(true);
          // Stagger the progress bar animations
          skills.forEach((_, index) => {
            setTimeout(() => {
              setVisibleBars(prev => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              });
            }, index * 200);
          });
        }
      },
      { threshold: 0.3 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const CircularProgress = ({ percentage, size = 120, strokeWidth = 8, color }: {
    percentage: number;
    size?: number;
    strokeWidth?: number;
    color: string;
  }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    
    return (
      <div className="relative flex items-center justify-center">
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="currentColor"
            strokeWidth={strokeWidth}
            fill="none"
            className="text-gray-200"
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            stroke="url(#gradient)"
            strokeWidth={strokeWidth}
            fill="none"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={animate ? { strokeDashoffset: circumference * (1 - percentage / 100) } : {}}
            transition={{ duration: 2, ease: "easeInOut" }}
          />
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" className="text-orange-500" stopColor="currentColor" />
              <stop offset="100%" className="text-pink-500" stopColor="currentColor" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <motion.span
            initial={{ opacity: 0 }}
            animate={animate ? { opacity: 1 } : {}}
            transition={{ delay: 1.5, duration: 0.5 }}
            className="text-2xl font-bold text-gray-700"
          >
            {animate ? `${percentage}%` : '0%'}
          </motion.span>
        </div>
      </div>
    );
  };

  return (
    <section 
      id="skills" 
      ref={sectionRef}
      className="py-20 bg-slate-100"
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            viewport={{ once: true }}
            className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium bg-indigo-100 text-indigo-600 border border-indigo-200 mb-4"
          >
            <Check /> Technical Expertise
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Skills & <span className="text-blue-600">Technologies</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
            5+ years of experience across modern web technologies, mobile development, 
            and scalable backend solutions for companies worldwide.
          </p>
        </motion.div>
        
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-8">
          {skills.map((skill, index) => {
            const IconComponent = skill.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1, duration: 0.6 }}
                viewport={{ once: true }}
                whileHover={{ y: -5 }}
              >
                <Card className="h-full bg-white/80 backdrop-blur-sm border-0 shadow-xl hover:shadow-2xl transition-all duration-300 group">
                  <CardHeader className="text-center pb-4">
                    <div className="flex flex-col items-center space-y-4">
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className={`w-16 h-16 rounded-2xl ${skill.color} flex items-center justify-center shadow-lg`}
                      >
                        <IconComponent className="h-8 w-8 text-white" />
                      </motion.div>
                      
                      <CircularProgress 
                        percentage={skill.percentage} 
                        color={skill.color}
                      />
                    </div>
                    
                    <CardTitle className="text-xl font-bold text-gray-900 group-hover:text-purple-600 transition-colors">
                      {skill.title}
                    </CardTitle>
                  </CardHeader>
                  
                  <CardContent className="text-center">
                    <p className="text-gray-600 mb-4 leading-relaxed">
                      {skill.description}
                    </p>
                    
                    <div className="flex flex-wrap gap-2 justify-center">
                      {skill.technologies.map((tech, techIndex) => (
                        <span
                          key={techIndex}
                          className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm font-medium"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Skill Progress Bars Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.6 }}
          viewport={{ once: true }}
          className="mt-20"
        >
          <div className="bg-white rounded-3xl p-8 shadow-xl">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Technical Proficiency
            </h3>
            <div className="grid md:grid-cols-2 gap-6">
              {skills.map((skill, index) => (
                <div key={index} className="space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">{skill.title}</span>
                    <span className="font-bold text-gray-900">{skill.percentage}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <motion.div
                      className={`h-full ${skill.color} rounded-full`}
                      initial={{ width: 0 }}
                      animate={visibleBars[index] ? { width: `${skill.percentage}%` } : {}}
                      transition={{ duration: 1.5, ease: "easeOut" }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Skills;